import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import config from '../config';
import Icon from './icons/Icon';
import OrganizationSelector from './OrganizationSelector';
import UserAvatar from './UserAvatar';
import NotificationBell from './notifications/NotificationBell';
import NewsButton from './news/NewsButton';
import './NavigationSidebar.css';

const NavigationSidebar = ({
  currentPath,
  onNavigate,
  onNewChat,
  tenants = [],
  selectedTenant = null,
  onTenantChange,
  isLoadingTenants = false,
  isCollapsed = false,
  user = null,
  onToggleCollapse,
  onOpenSearch,
  playbooks = [],
  hasGitConnection = false,
}) => {
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [showNoGitPopup, setShowNoGitPopup] = useState(false);
  const [noGitPopupAnchor, setNoGitPopupAnchor] = useState(null);
  const [playbooksCollapsed, setPlaybooksCollapsed] = useState(() => {
    return localStorage.getItem('nav-playbooks-collapsed') === 'true';
  });
  const [projectsCollapsed, setProjectsCollapsed] = useState(() => {
    return localStorage.getItem('nav-projects-collapsed') === 'true';
  });
  const [resourcesCollapsed, setResourcesCollapsed] = useState(() => {
    return localStorage.getItem('nav-resources-collapsed') === 'true';
  });
  const [showResourceFeatures, setShowResourceFeatures] = useState(() => {
    const saved = localStorage.getItem('showResourceFeatures');
    return saved === null ? true : saved === 'true';
  });
  const [showToolsPage, setShowToolsPage] = useState(() => {
    const saved = localStorage.getItem('showToolsPage');
    return saved === null ? true : saved === 'true';
  });
  const [showSearch, setShowSearch] = useState(() => {
    const saved = localStorage.getItem('showSearch');
    return saved === null ? false : saved === 'true';
  });
  const [showNotifications, setShowNotifications] = useState(() => {
    const saved = localStorage.getItem('showNotifications');
    return saved === null ? false : saved === 'true';
  });
  const [showFavorites, setShowFavorites] = useState(() => {
    const saved = localStorage.getItem('showFavorites');
    return saved === null ? false : saved === 'true';
  });

  // Active playbook badge — set when a playbook is applied in Chat/Code
  const [activePlaybook, setActivePlaybook] = useState(null);

  useEffect(() => {
    const handleActivePlaybook = (e) => setActivePlaybook(e.detail || null);
    window.addEventListener('activePlaybookChanged', handleActivePlaybook);
    return () => window.removeEventListener('activePlaybookChanged', handleActivePlaybook);
  }, []);

  // Listen for feature toggle changes
  useEffect(() => {
    const handleFeatureToggle = (event) => {
      if (event.detail && 'showResourceFeatures' in event.detail) {
        setShowResourceFeatures(event.detail.showResourceFeatures);
      }
      if (event.detail && 'showToolsPage' in event.detail) {
        setShowToolsPage(event.detail.showToolsPage);
      }
      // FIXME: remove it when the authentication will work, now don't works because it needs the jwt of mia and not okta
      if (event.detail && 'showSearch' in event.detail) {
        setShowSearch(event.detail.showSearch);
      }
      if (event.detail && 'showNotifications' in event.detail) {
        setShowNotifications(event.detail.showNotifications);
      }
      if (event.detail && 'showFavorites' in event.detail) {
        setShowFavorites(event.detail.showFavorites);
      }
    };
    window.addEventListener('featureToggleChanged', handleFeatureToggle);
    return () => window.removeEventListener('featureToggleChanged', handleFeatureToggle);
  }, []);

  const isActive = (path) => {
    if (path === '/') return currentPath === '/';
    if (path === '/chat') return currentPath.startsWith('/chat');
    return currentPath.startsWith(path);
  };

  const allNavItems = [
    { icon: 'home', label: 'Home', path: '/', section: null },
    { icon: 'message', label: 'Chat', path: '/compose', section: null },
    { icon: 'code', label: 'Code', path: '/chat/', section: null },
    { icon: 'clock', label: 'Memories', path: '/memories', section: null },
    { icon: 'search', label: 'Search', path: '/search', section: null, keyboard: '⌘+K', featureFlag: 'search' },

    { section: 'Projects' },
    { icon: 'grid', label: 'All projects', path: '/projects', section: 'projects' },
    { icon: 'compass', label: 'Discover', path: '/discover', section: 'projects', featureFlag: 'resourceFeatures' },
    { icon: 'star', label: 'Starred', path: '/projects/starred', section: 'projects', featureFlag: 'favorites' },
    { icon: 'users', label: 'Shared with me', path: '/projects/shared', section: 'projects', featureFlag: 'favorites' },

    { section: 'Resources' },
    { icon: 'settings', label: 'Connected Tools', path: '/tools', section: 'resources', featureFlag: 'toolsPage' },
    { icon: 'package', label: 'AI Playbook Library', section: 'resources', externalHref: config.aiFoundryBaseUrl ? `${config.aiFoundryBaseUrl}/website/playbook` : null },
    { icon: 'book', label: 'Learn', path: '/learn', section: 'resources' },
  ];

  // Filter items based on feature toggles
  const navItems = allNavItems.filter(item => {
    if (item.featureFlag === 'resourceFeatures' && !showResourceFeatures) {
      return false;
    }
    if (item.featureFlag === 'toolsPage' && !showToolsPage) {
      return false;
    }
    if (item.featureFlag === 'search' && !showSearch) {
      return false;
    }
    if (item.featureFlag === 'favorites' && !showFavorites) {
      return false;
    }
    return true;
  });

  return (
    <nav className={`navigation-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* No-git-connection popup */}
      {showNoGitPopup && noGitPopupAnchor && (
        <div
          className="nav-no-git-popup"
          style={{ top: noGitPopupAnchor.top, left: noGitPopupAnchor.left }}
        >
          <Icon name="warning" size={16} />
          <div className="nav-no-git-popup-content">
            <span>Connect a Git provider to use Code.</span>
            <button
              className="nav-no-git-popup-link"
              onClick={() => {
                setShowNoGitPopup(false);
                onNavigate('/tools');
              }}
            >
              Connect now →
            </button>
          </div>
          <button className="nav-no-git-popup-close" onClick={() => setShowNoGitPopup(false)}>
            <Icon name="x" size={14} />
          </button>
        </div>
      )}
      {/* Logo */}
      <div className="nav-header">
        <button
          className="nav-logo"
          onClick={() => isCollapsed ? onToggleCollapse() : onNavigate('/')}
          title={isCollapsed ? 'Expand sidebar' : 'Go to home'}
        >
          <div className="mia-logo">
            <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
              <path d="M 10 28 C 10 28, 10 18, 15 13 C 20 8, 25 8, 30 13 C 35 18, 35 28, 35 28 C 35 28, 30 31, 25 28 C 20 25, 20 18, 25 15 C 30 12, 35 15, 40 21 C 45 27, 45 35, 45 35"
                    fill="none"
                    stroke="url(#miaWavyGradient)"
                    strokeWidth="4.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"/>
              <defs>
                <linearGradient id="miaWavyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#5B9FD5"/>
                  <stop offset="50%" stopColor="#60a5fa"/>
                  <stop offset="100%" stopColor="#7BB3E0"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          {!isCollapsed && (
            <div className="nav-logo-label">
              <span className="nav-logo-text">Flow</span>
              <span className="nav-logo-beta">beta</span>
            </div>
          )}
        </button>
        {!isCollapsed ? (
          <button
            className="nav-minimize"
            onClick={onToggleCollapse}
            title="Collapse sidebar"
          >
            <Icon name="panel-left" size={18} />
          </button>
        ) : (
          <button
            className="nav-expand"
            onClick={onToggleCollapse}
            title="Expand sidebar"
          >
            <Icon name="panel-right" size={18} />
          </button>
        )}
      </div>

      {/* Workspace selector */}
      {!isCollapsed && (
        <div className="workspace-selector">
          <button
            className="workspace-button"
            onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)}
          >
            <div className="workspace-avatar">
              <span>{selectedTenant?.name?.charAt(0).toUpperCase() || 'M'}</span>
            </div>
            <div className="workspace-info">
              <div className="workspace-name">
                {isLoadingTenants ? 'Loading...' : (selectedTenant?.name || 'Select Tenant')}
              </div>
              <Icon name="chevron-down" size={16} />
            </div>
          </button>

        {/* Tenant dropdown */}
        {isTenantDropdownOpen && tenants.length > 0 && (
          <>
            <div
              className="dropdown-overlay"
              onClick={() => setIsTenantDropdownOpen(false)}
            />
            <div className="tenant-dropdown">
              {tenants.map((tenant) => (
                <button
                  key={tenant.tenantId}
                  className={`tenant-option ${selectedTenant?.tenantId === tenant.tenantId ? 'active' : ''}`}
                  onClick={() => {
                    onTenantChange(tenant);
                    setIsTenantDropdownOpen(false);
                  }}
                >
                  <div className="tenant-avatar">
                    <span>{tenant.name?.charAt(0).toUpperCase() || 'T'}</span>
                  </div>
                  <div className="tenant-info">
                    <div className="tenant-name">{tenant.name || tenant.tenantId}</div>
                    {tenant.description && (
                      <div className="tenant-description">{tenant.description}</div>
                    )}
                  </div>
                  {selectedTenant?.tenantId === tenant.tenantId && (
                    <Icon name="check-circle" size={16} className="tenant-check" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
        </div>
      )}

      {/* Authorization organization selector (populates x-mia-acl-context) —
          distinct from the Console company selector above. */}
      {!isCollapsed && <OrganizationSelector />}

      {/* Navigation items */}
      <div className="nav-items">
        {/* ── Always visible: Home, Chat, Code ── */}
        {navItems.filter(item => !item.section).map((item, index) => {
          // Show active playbook badge on Chat (/compose) and Code (/chat/) items
          const isChatItem = item.path === '/compose';
          const isCodeItem = item.path === '/chat/';
          const showPlaybookBadge = activePlaybook && (
            (isChatItem && activePlaybook.mode !== 'code') ||
            (isCodeItem && activePlaybook.mode === 'code')
          );
          const isCodeDisabled = isCodeItem && !hasGitConnection;
          return (
          <button
            key={index}
            className={`nav-item ${isActive(item.path) ? 'active' : ''} ${isCodeDisabled ? 'no-git-connection' : ''}`}
            onClick={(e) => {
              if (isCodeDisabled) {
                const rect = e.currentTarget.getBoundingClientRect();
                setNoGitPopupAnchor({ top: rect.top, left: rect.right + 8 });
                setShowNoGitPopup(true);
                setTimeout(() => setShowNoGitPopup(false), 4000);
                return;
              }
              if (item.path === '/search' && onOpenSearch) {
                onOpenSearch();
              } else {
                onNavigate(item.path);
              }
            }}
            title={isCodeDisabled ? '' : (isCollapsed ? item.label : (showPlaybookBadge ? `${item.label} — ${activePlaybook.name}` : ''))}
          >
            <Icon name={item.icon} size={20} />
            {!isCollapsed && (
              <>
                <div className="nav-item-label-group">
                  <span className="nav-item-label">{item.label}</span>
                  {showPlaybookBadge && (
                    <span className="nav-item-playbook-badge" title={activePlaybook.name}>
                      {activePlaybook.name}
                    </span>
                  )}
                </div>
                {item.keyboard && (
                  <span className="nav-item-keyboard">{item.keyboard}</span>
                )}
              </>
            )}
          </button>
          );
        })}

        {/* ── My AI Playbooks (collapsible) ── */}
        {isCollapsed ? (
          playbooks.length > 0 && (
            <>
              <div className="nav-section-divider" />
              {playbooks.map(playbook => {
                const isCode = playbook.mode === 'code';
                const playbookPath = `/playbook/${playbook.itemId}`;
                return (
                  <button
                    key={playbook.itemId || playbook.name}
                    className={`nav-item ${currentPath.startsWith(playbookPath) ? 'active' : ''}`}
                    onClick={() => onNavigate(playbookPath)}
                    title={playbook.name}
                  >
                    <Icon name={isCode ? 'code' : 'message'} size={20} />
                  </button>
                );
              })}
            </>
          )
        ) : (
          <div className="nav-playbooks-section">
            <button
              className="nav-section-title nav-section-toggle"
              onClick={() => {
                const next = !playbooksCollapsed;
                setPlaybooksCollapsed(next);
                localStorage.setItem('nav-playbooks-collapsed', String(next));
              }}
            >
              <span>My Playbooks</span>
              <Icon name={playbooksCollapsed ? 'chevron-right' : 'chevron-down'} size={12} />
            </button>
            {!playbooksCollapsed && (
              playbooks.length > 0 && (
                playbooks.map(playbook => {
                  const isCode = playbook.mode === 'code';
                  const playbookPath = `/playbook/${playbook.itemId}`;
                  return (
                    <button
                      key={playbook.itemId || playbook.name}
                      className={`nav-playbook-item ${currentPath.startsWith(playbookPath) ? 'active' : ''}`}
                      onClick={() => onNavigate(playbookPath)}
                      title={playbook.description || playbook.name}
                    >
                      <span className="nav-playbook-item-dot" />
                      <span className="nav-playbook-item-label">{playbook.name}</span>
                      <span className="nav-playbook-item-mode">
                        <Icon name={isCode ? 'code' : 'message'} size={20} />
                      </span>
                    </button>
                  );
                })
              )
            )}
          </div>
        )}

        {/* ── Projects (collapsible) ── */}
        {isCollapsed ? (
          <>
            <div className="nav-section-divider" />
            {navItems.filter(item => item.section === 'projects').map((item, index) => (
              <button
                key={`proj-${index}`}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => onNavigate(item.path)}
                title={item.label}
              >
                <Icon name={item.icon} size={20} />
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              className="nav-section-title nav-section-toggle"
              onClick={() => {
                const next = !projectsCollapsed;
                setProjectsCollapsed(next);
                localStorage.setItem('nav-projects-collapsed', String(next));
              }}
            >
              <span>Projects</span>
              <Icon name={projectsCollapsed ? 'chevron-right' : 'chevron-down'} size={12} />
            </button>
            {!projectsCollapsed && navItems.filter(item => item.section === 'projects').map((item, index) => (
              <button
                key={`proj-${index}`}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => onNavigate(item.path)}
              >
                <Icon name={item.icon} size={20} />
                <span className="nav-item-label">{item.label}</span>
              </button>
            ))}
          </>
        )}

        {/* ── Resources (collapsible) ── */}
        {isCollapsed ? (
          <>
            <div className="nav-section-divider" />
            {navItems.filter(item => item.section === 'resources').map((item, index) => (
              <button
                key={`res-${index}`}
                className={`nav-item ${!item.externalHref && isActive(item.path) ? 'active' : ''}`}
                onClick={() => item.externalHref ? window.open(item.externalHref, '_blank', 'noopener,noreferrer') : onNavigate(item.path)}
                title={item.label}
              >
                <Icon name={item.icon} size={20} />
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              className="nav-section-title nav-section-toggle"
              onClick={() => {
                const next = !resourcesCollapsed;
                setResourcesCollapsed(next);
                localStorage.setItem('nav-resources-collapsed', String(next));
              }}
            >
              <span>Resources</span>
              <Icon name={resourcesCollapsed ? 'chevron-right' : 'chevron-down'} size={12} />
            </button>
            {!resourcesCollapsed && navItems.filter(item => item.section === 'resources').map((item, index) => (
              <button
                key={`res-${index}`}
                className={`nav-item ${!item.externalHref && isActive(item.path) ? 'active' : ''}`}
                onClick={() => item.externalHref ? window.open(item.externalHref, '_blank', 'noopener,noreferrer') : onNavigate(item.path)}
              >
                <Icon name={item.icon} size={20} />
                <span className="nav-item-label">{item.label}</span>
                {item.externalHref && <Icon name="external-link" size={12} className="nav-item-external-icon" />}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Bottom actions */}
      <div className="nav-footer">
        <div className="nav-user">
          <UserAvatar
            onClick={() => onNavigate('/settings')}
            size={isCollapsed ? 32 : 40}
            user={user}
          />
          {!isCollapsed && (
            <div className="nav-actions">
              <NewsButton />
              {showNotifications && (
                <div className="nav-notification">
                  <NotificationBell />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

NavigationSidebar.propTypes = {
  currentPath: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  tenants: PropTypes.array,
  selectedTenant: PropTypes.object,
  onTenantChange: PropTypes.func,
  isLoadingTenants: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  user: PropTypes.object,
  onOpenSearch: PropTypes.func,
  playbooks: PropTypes.array,
  hasGitConnection: PropTypes.bool,
};

export default NavigationSidebar;
